import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel from "../../../../../widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import SnGrid from "@shared/components/Grid/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SharedForm from "../../../../../widgets/SharedForm";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SN_MAS_ORG_REG_002_POP_01 from "./SN_MAS_ORG_REG_002_POP_01";
import { showPopupModal } from "@widgets/CommonModal";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SnTab from "@shared/components/SnTab";

interface Props {
  Menuprops: menuInfo;
}

// 로고·직인 파일명 길이 제한. mas_biz.nm_logo / nm_seal 에는 파일명만 저장하므로 칼럼 길이는 여유가 있다.
// 200 은 첨부 원본이 들어가는 sys_attachfile.nm_file 에 맞춘 값이라 그대로 둔다.
const MAX_FILE_NAME_LEN = 200;

interface RowData {
  KeyField?: string;

  [key: string]: any;
}

const SN_MAS_ORG_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region Variable
  const { userInfo } = global();
  const searchRef = useRef<any>(null);
  const gridref1 = useRef<any>(null);
  const SharedFormref1 = useRef<any>();
  const sheetref1 = useRef<any>(null); // excel download 용
  //#endregion

  //#region Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<any>();
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [focusedRowData, setFocusedRowData] = useState<any>();
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_MAS_ORG_REG_002";
  const defaultSize = { panel1width: "23.5%", panel2width: "76.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums(rtn.grid.grid1);
        setSharedForm(rtn.form.freeform1);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridref1.current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  //region onSearch
  const onSearch = async () => {
    try {
      const modifiedRowsH = gridref1.current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const result: any = await onSave();
          if (!result) return;
        } else {
          gridref1.current.resetUpdate();
        }
      }

      const params = {
        id: "sn_mas_org_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      setFocusedRowData(null);
      setDataSource1([]);
      const data: any = await fnGetDataSet(params);
      setDataSource1(data[0]);
      if (data[0] && data[0].length > 0) {
        setGridFocus(true);
      }
    } catch (error) {}
  };

  //region onInsert
  const onInsert = async () => {
    try {
      const newRow = {};
      if (gridref1.current) gridref1.current.addRow(newRow);
    } catch (e) {}
  };

  //region onBeforeAddRowH
  const onBeforeAddRowH = (newRow: RowData): boolean => {
    try {
      const masCcChanges = gridref1.current.getChange().insert;

      //if (masCcChanges.length <= 0) return false;

      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        yn_use: "Y",
        yn_utax: "N",
        am_security: 1.0,
        fg_firm: "1",
        fg_biz: "0",
        no_seq: 1,
        yn_grw: "Y",
      };
      Object.assign(newRow, updatedRow); // 원본 객체 수정

      return true;
    } catch (error: any) {
      return false;
    }
  };

  //region onDelete
  const onDelete = async () => {
    try {
      gridref1.current.deleteSelectedRows();

      const rowCount = gridref1.current?.api.getDisplayedRowCount();
      if (!rowCount) setFocusedRowData(null);
    } catch (error) {}
  };

  //region onSave
  const onSave = async () => {
    try {
      const requiredValuesCheck = gridref1.current.requiredValuesCheck();
      if (!requiredValuesCheck) {
        return;
      }

      const gridGetChanges = gridref1.current.getChange();
      if (gridGetChanges.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }

      if (gridGetChanges.update) {
        gridGetChanges.update = gridGetChanges.update.map((row: any) => ({
          ...row,
          cd_user_amd: userInfo.cd_user,
        }));
      }

      const saveData = {
        mas_biz: gridGetChanges,
      };
      const result = await fnSave("SN_MAS_ORG_REG_002_SAVE_01", saveData);
      gridref1.current.resetUpdate(!result);
      return result;
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  //region onFocusedRowChanged
  const onFocusedRowChanged = useCallback(
    (focusedRowData: any | null) => {
      setFocusedRowData(focusedRowData);
    },
    [focusedRowData]
  );
  //#endregion

  const onButtonClick = async (id: string) => {
    try {
      const selectedRows = gridref1.current?.getSelectedRows();
      if (!selectedRows.length) return;
      switch (id) {
        case "btnBizzMain":
          // const cdBiz = selectedRows[0].cd_biz_tax || "";
          const component = <SN_MAS_ORG_REG_002_POP_01 cdBiz={""} />;
          const result = await showPopupModal({
            title: "주종사업장조회 도움창",
            children: component,
          });
          if (!result || result === "close") return;
          const rtnResult = result[1];
          const cd_biz_tax = rtnResult[0].cd_biz;
          console.log(cd_biz_tax);

          const gridApi = gridref1.current.api;
          const focusedCell = gridApi.getFocusedCell();
          if (focusedCell) {
            const rowNode = gridApi.getDisplayedRowAtIndex(
              focusedCell.rowIndex
            );
            gridref1.current.setValue(rowNode, "cd_biz_tax", cd_biz_tax);
            setFocusedRowData((prev: any) => ({
              ...prev,
              cd_biz_tax: cd_biz_tax,
            }));
          }
          break;

        case "btnbiztype":
          // const component2 = <SN_MAS_ORG_REG_002_POP_02 />;
          // const result2 = await showPopupModal("주업종코드 도움창", component2);
          // if (!result2 || result2 === 'close') return;
          // const rtnData = result2[0];
          // // updateGridData({
          // //   cd_biz_type: rtnData[0].cd_idy,
          // //   // nm_idy: rtnData[0].nm_idy,
          // // });
          // updateGridData("cd_biz_type", rtnData[0].cd_idy);
          // updateGridData("nm_idy", rtnData[0].nm_idy);
          break;

        case "btntax":
          // const component3 = <SN_MAS_ORG_REG_002_POP_03 />;
          // const rslt = await showPopupModal("세무서코드 도움창", component3);
          // if (!rslt || rslt === "close") return;

          // const rtnData2 = rslt[0];
          // updateGridData("cd_tax", rtnData2[0].cd_taxorg);
          // updateGridData("nm_tax", rtnData2[0].nm_taxorg);
          break;

        case "btnadmin":
          // const component4 = <SN_MAS_ORG_REG_002_POP_04 />;
          // const rslt2 = await showPopupModal("행정동코드 도움창", component4);
          // if (!rslt2 || rslt2 === "close") return;

          // const rtnData3 = rslt2[0];
          // updateGridData("cd_admin", rtnData3[0].cd_gorg);
          // updateGridData("nm_admin", rtnData3[0].nm_gorg_h);
          break;

        case "btnlgat":
          // const component5 = <SN_MAS_ORG_REG_002_POP_05 />;
          // const rslt3 = await showPopupModal("법정동코드 도움창", component5);
          // if (!rslt3 || rslt3 === "close") return;

          // const rtnData4 = rslt3[0];
          // updateGridData("cd_lgat", rtnData4[0].cd_gorg);
          // updateGridData("nm_lgat", rtnData4[0].nm_gorg_b);
          break;

        case "bnfirml":
          // const component6 = <SN_MAS_ORG_REG_002_POP_06 />;
          // const rslt4 = await showPopupModal(
          //   "법인구분(지방세) 도움창",
          //   component6
          // );
          // if (!rslt4 || rslt4 === "close") return;

          // const rtnData5 = rslt4[0];
          // updateGridData("fg_firm_l", rtnData5[0].cd_flag);
          // updateGridData("nm_firm_l", rtnData5[0].nm_flag);
          break;

        case "addlogo":
          break;
        case "deletelogo":
          break;
        case "addseal":
          break;
        case "deleteseal":
          break;
      }
    } catch (error) {}
  };

  // 첨부파일 문서번호: 저장 전 신규 행은 비워서 업로드를 막는다
  const bizDocu =
    focusedRowData?.state === "insert" ? "" : focusedRowData?.cd_biz || "";

  // 라인별 첨부 건수 (로고·직인은 1건만 허용). drop 시 동기로 판단해야 해서 ref 로 둔다.
  const fileCountRef = useRef<Record<number, number>>({});
  useEffect(() => {
    fileCountRef.current = {};
    if (!bizDocu) return;
    let canceled = false;
    [1, 2].forEach(async (no_line) => {
      const data: any = await fnGetDataSet({
        id: "com_attachfile_01",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_menu: cd_menu,
          no_docu: bizDocu,
          no_line_docu: no_line,
        },
      });
      if (!canceled) fileCountRef.current[no_line] = data?.[0]?.length || 0;
    });
    return () => {
      canceled = true;
    };
  }, [bizDocu]);

  // 첨부한 로고(라인 1)·직인(라인 2)의 파일명을 mas_biz.nm_logo / nm_seal 에 저장한다.
  // 저장 경로는 서버가 테넌트 기준으로 정하므로 화면은 파일명만 넘긴다.
  // im_logo / im_seal (bytea) 는 파워빌더 컨버전 잔재라 더 이상 쓰지 않는다 —
  // 페이로드에 키를 넣지 않으면 MapStruct 가 건드리지 않는다(null 을 넣으면 컬럼을 비운다).
  // uploaded 가 null 이면 파일명을 비운다.
  const saveBizFile = async (no_line: number, uploaded: any | null) => {
    if (!bizDocu) return;
    const nmCol = no_line === 1 ? "nm_logo" : "nm_seal";
    const values = { [nmCol]: uploaded?.nm_file || null };
    const ok = await fnSave(
      "SN_MAS_ORG_REG_002_SAVE_01",
      {
        mas_biz: {
          insert: [],
          update: [{ cd_firm: userInfo.cd_firm, cd_biz: bizDocu, ...values }],
          delete: [],
        },
      },
      undefined,
      { silentSuccess: true }
    );
    if (!ok) return;
    // 그리드 행에도 반영(변경 표시 없이) — 이후 다른 항목 저장 시 예전 파일명으로 덮어쓰지 않게
    gridref1.current?.api?.forEachNode((node: any) => {
      if (node.data?.cd_biz === bizDocu) node.data[nmCol] = values[nmCol] ?? "";
    });
  };

  const onValueChaged = (data: any, param: string, formData: any) => {
    if (param === "no_post") {
      return { dc_address: data.dc_address };
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            seq={1}
            ref={gridref1}
            enableAddRow={true}
            columnConfig={columns} // 컬럼 설정 전달
            rowData={dataSource1}
            enableExternalChart={false}
            isEditable={false}
            rowSelection="single" // 단일 선택 모드
            onFocusedRowChanged={onFocusedRowChanged}
            onBeforeAddRow={onBeforeAddRowH}
            cellSelection={false}
            title="사업장정보"
            resizeController={{
              defaultSize: defaultSize,
              onResizeStop: onResizeStop,
            }}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <div className="h-full flex flex-col min-h-0">
            <div className="flex-1 min-h-0">
              <SharedForm
                ref={SharedFormref1}
                data={focusedRowData}
                controls={sharedForm}
                gridref={gridref1}
                title="상세정보"
                onButtonClick={onButtonClick}
                onValueChaged={onValueChaged}
              ></SharedForm>
            </div>
            {/* 사업장 로고·직인 첨부: 사업장코드 기준. 출력물에서 구분하도록 라인번호로 나눈다 (1 로고, 2 직인) */}
            <div className="mt-1 shrink-0 flex space-x-1.5">
              {[
                { no_line: 1, label: "로고" },
                { no_line: 2, label: "직인" },
              ].map(({ no_line, label }) => (
                <div
                  key={no_line}
                  className="flex-1 min-w-0"
                  // 첨부 칸 drop 보다 먼저 실행: 파일명 길이·1건 제한
                  onDropCapture={(e) => {
                    const files = Array.from(e.dataTransfer.files);
                    const tooLong = files.find(
                      (f) => f.name.length > MAX_FILE_NAME_LEN
                    );
                    if (tooLong) {
                      e.preventDefault();
                      e.stopPropagation();
                      showDialog(
                        "alter",
                        `${label} 파일명은 ${MAX_FILE_NAME_LEN}자까지 등록하실 수 있습니다.\n파일명을 조금 줄여서 다시 올려주시기 바랍니다.\n(현재 ${tooLong.name.length}자: ${tooLong.name})`
                      );
                      return;
                    }
                    // 1건만 허용: 여러 파일을 한 번에 놓거나 이미 있으면 막는다 (신규 행은 첨부 칸이 안내)
                    if (
                      bizDocu &&
                      (files.length > 1 ||
                        (fileCountRef.current[no_line] || 0) > 0)
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      showDialog(
                        "alter",
                        files.length > 1
                          ? `${label}는 1건만 등록하실 수 있습니다.\n파일을 하나씩 올려주시기 바랍니다.`
                          : `${label}가 이미 등록되어 있습니다.\n기존 파일을 삭제하신 후 다시 올려주시기 바랍니다.`
                      );
                      return;
                    }
                  }}
                >
                  <SnTab
                    titles={[`   ${label}   `]}
                    height="auto"
                    body={[
                      <SnFileUploadBox
                        key="file"
                        selectedDocu={bizDocu}
                        selectedLineDocu={no_line}
                        onUploaded={(files) => {
                          fileCountRef.current[no_line] =
                            (fileCountRef.current[no_line] || 0) + files.length;
                          return saveBizFile(no_line, files[files.length - 1]);
                        }}
                        onDeleted={(file) => {
                          fileCountRef.current[no_line] = Math.max(
                            (fileCountRef.current[no_line] || 0) - 1,
                            0
                          );
                          // 저장된 파일을 지웠을 때만 비운다
                          const nmCol = no_line === 1 ? "nm_logo" : "nm_seal";
                          let saved = "";
                          gridref1.current?.api?.forEachNode((node: any) => {
                            if (node.data?.cd_biz === bizDocu)
                              saved = node.data[nmCol] || "";
                          });
                          if (saved === file.nm_file)
                            return saveBizFile(no_line, null);
                        }}
                      />,
                    ]}
                  />
                </div>
              ))}
            </div>
          </div>
        </SnResizable>
      </div>
    </div>
  );
});

export default SN_MAS_ORG_REG_002;
